import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const routeTree = [
  {
    pathPattern: new RegExp("/analytics"),
    path: (_) => `/analytics`,
    name: (_) => `Analytics`,
    children: [
      {
        pathPattern: new RegExp("/device-clear"),
        path: (_) => `/device-clear`,
        name: (_) => `Device Clear`,
        children: [],
      },
      {
        pathPattern: new RegExp("/device-attention"),
        path: (_) => `/device-attention`,
        name: (_) => `Device Attention`,
        children: [],
      },
      {
        pathPattern: new RegExp("/device-down"),
        path: (_) => `/device-down`,
        name: (_) => `Device Down`,
        children: [],
      },
      {
        pathPattern: new RegExp("/internet-online"),
        path: (_) => `/internet-online`,
        name: (_) => `Internet Online`,
        children: [],
      },
      {
        pathPattern: new RegExp("/internet-offline"),
        path: (_) => `/internet-offline`,
        name: (_) => `Internet Offline`,
        children: [],
      },
      {
        pathPattern: new RegExp("/internet-suspend"),
        path: (_) => `/internet-suspend`,
        name: (_) => `Internet Suspend`,
        children: [],
      },
      {
        pathPattern: new RegExp("/internet-technical-issue"),
        path: (_) => `/internet-technical-issue`,
        name: (_) => `Internet Technical Issue`,
        children: [],
      },
      {
        pathPattern: new RegExp("/analytics/device-detail-analysis/(?<type>\\w+)/(?<id>\\d+)"),
        path: ({ type, id }) => `/device-detail-analysis/${type}/${id}`,
        name: ({ type, id }) => `Device Detail Analysis`,
        children: [
          {
            pathPattern: new RegExp(
              "/component-detail/(?<resource>[a-zA-Z_]+)/(?<componentId>\\d+)"
            ),
            path: ({ resource, componentId }) =>
              `/component-detail/${resource}/${componentId}`,
            name: ({ resource, componentId }) =>
              `${
                resource === "cpu" || resource === "cpu_user" || resource === "cpu_system" 
                  ? "CPU"
                  : resource[0].toUpperCase() + resource.slice(1)
              } Detail`,
            children: [],
          },
          {
            pathPattern: new RegExp("/qos-detail/(?<type>[a-zA-Z_]+)"),
            path: ({ type }) => `/qos-detail/${type}`,
            name: ({ type }) => `QoS (${type})`,
            children: [],
          },
          {
            pathPattern: new RegExp("/traffic-detail/(?<interfaceId>\\d+)"),
            path: ({ interfaceId }) => `/traffic-detail/${interfaceId}`,
            name: ({ interfaceId }) => `Traffic Detail`,
            children: [],
          },
          {
            pathPattern: new RegExp(
              "/capacity-forecast/(?<resource>[a-zA-Z_]+)/(?<forecastId>\\d+)"
            ),
            path: ({ resource, forecastId }) =>
              `/capacity-forecast/${resource}/${forecastId}`,
            name: ({ resource, forecastId }) =>
              `Capacity Forecast - ${
                resource === "cpu"
                  ? resource.toUpperCase()
                  : resource[0].toUpperCase() + resource.slice(1)
              }`,
            children: [],
          },
        ],
      },
    ],
  },
  {
    pathPattern: new RegExp("/alarms"),
    path: (_) => `/alarms`,
    name: (_) => `Alarms`,
    children: [
      {
        pathPattern: new RegExp("/read"),
        path: (_) => `/read`,
        name: (_) => `Alarms (read)`,
        children: [],
      },
      {
        pathPattern: new RegExp("/unread"),
        path: (_) => `/unread`,
        name: (_) => `Alarms (unread)`,
        children: [],
      },
      {
        pathPattern: new RegExp("/(?<id>\\d+)"),
        path: ({ id }) => `/${id}`,
        name: ({ id }) => `Alarm Detail`,
        children: [],
      },
    ],
  },
  {
    pathPattern: new RegExp("/settings"),
    path: (_) => `/settings`,
    name: (_) => `Settings`,
    children: [
      {
        pathPattern: new RegExp("/faq"),
        path: (_) => `/faq`,
        name: (_) => `FAQ`,
        children: [],
      },
      {
        pathPattern: new RegExp("/about-us"),
        path: (_) => `/about-us`,
        name: (_) => `About Us`,
        children: [],
      },
    ],
  },
];

const removeEndingSlash = (url) => {
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

const parsePath = (path) => {
  try {
    const result = [];

    let candidate = routeTree;
    let currentPath = path;
    while (currentPath) {
      let noMatch = true;
      for (const node of candidate) {
        const match = path.match(node.pathPattern);
        if (match) {
          noMatch = false;
          result.push({
            path: node.path(match.groups),
            name: node.name(match.groups),
          });

          currentPath = currentPath.slice(match[0].length);

          if (currentPath && node.children.length === 0) {
            console.log(
              `Invalid path, path longer than any valid paths: ${path}`
            );
            return [];
          }
          candidate = node.children;
          break;
        }
      }

      if (noMatch) {
        console.log(
          `Invalid path, path does not match any of the valid paths: ${path}`
        );
        return [];
      }
    }

    const resultWithFullPath = [];
    let fullPath = "";
    for (const el of result) {
      fullPath += el.path;
      resultWithFullPath.push({
        name: el.name,
        fullPath: fullPath,
      });
    }
    return resultWithFullPath;
  } catch (error) {
    console.log(`Invalid path: ${path}, error:`, error);
    return [];
  }
};

export const Breadcrumbs = () => {
  const location = useLocation();
  const [parsedPath, setParsedPath] = useState();

  useEffect(() => {
    setParsedPath(parsePath(removeEndingSlash(location.pathname)));
  }, [location]);

  return (
    <div
      style={{
        padding: "2px 1rem",
        backgroundColor: "#F2F3F5",
        display: "flex",
        gap: "0.5rem",
        position: "fixed",
        width: "100%",
        zIndex: "100",
      }}
    >
      {parsedPath &&
        parsedPath.flatMap((item, index) => [
          <Link to={item.fullPath} key={`link-${index}`}>
            {item.name}
          </Link>,
          index < parsedPath.length - 1 && (
            <ChevronRightIcon key={`arrow-${index}`} width={"1.5rem"} />
          ),
        ])}
    </div>
  );
};
